import { useRef } from "react";
import type { TimelineEvent } from "../api/types";
import type {
  ActivityBlock,
  LaidOut,
  TimelineMarker,
} from "../lib/timelineSlots";
import {
  DAY_START_MIN,
  DAY_END_MIN,
  MARKER_PX,
  MIN_BLOCK_PX,
  PX_PER_MIN,
  STEP_MIN,
  buildBlocks,
  clampMin,
  layoutLanes,
  minToLabel,
  resolveSlotRange,
} from "../lib/timelineSlots";

interface Props {
  dayEvents: TimelineEvent[];
  isToday: boolean;
  onCreateSlot: (startMin: number, endMin: number) => void;
}

// Blocs et marqueurs passent dans le même calcul de lanes : sur le rail, un
// marqueur ne doit pas plus masquer un bloc qu'un bloc n'en masque un autre.
type BlockItem = ActivityBlock & { kind: "block" };
type MarkerItem = TimelineMarker & {
  kind: "marker";
  startMin: number;
  endMin: number;
  minPx: number;
};

/** Colonne d'un item dans son cluster ; 8px de gouttière de chaque côté du rail. */
function laneStyle(lane: number, lanes: number) {
  return {
    left: `calc(8px + (100% - 16px) * ${lane / lanes})`,
    width: `calc((100% - 16px) / ${lanes} - ${lanes > 1 ? 3 : 0}px)`,
  };
}

export function TimelineDayView({ dayEvents, isToday, onCreateSlot }: Props) {
  const trackRef = useRef<HTMLDivElement>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  // Drag imperatif : le rect est capturé une fois au pointerdown, le ghost est
  // peint directement en CSS (rAF) sans setState : aucun reflow ni re-render à
  // 60 fps pendant le move. React ne rend qu'au create final (onCreateSlot).
  const rectRef = useRef<DOMRect | null>(null);
  const fromRef = useRef<number | null>(null);
  const toRef = useRef(DAY_START_MIN);
  const rafRef = useRef<number | null>(null);

  const { blocks, markers } = buildBlocks(dayEvents, isToday);
  const laid = layoutLanes<BlockItem | MarkerItem>([
    ...blocks.map((b) => ({ ...b, kind: "block" as const })),
    ...markers.map((m) => ({
      ...m,
      kind: "marker" as const,
      startMin: m.min,
      endMin: m.min,
      minPx: MARKER_PX,
    })),
  ]);
  const laidBlocks = laid.filter(
    (i): i is LaidOut<BlockItem> => i.kind === "block",
  );
  const laidMarkers = laid.filter(
    (i): i is LaidOut<MarkerItem> => i.kind === "marker",
  );
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const height = (DAY_END_MIN - DAY_START_MIN) * PX_PER_MIN;

  function minFromClientY(clientY: number): number {
    const rect = rectRef.current;
    if (!rect) return DAY_START_MIN;
    const raw = DAY_START_MIN + (clientY - rect.top) / PX_PER_MIN;
    return clampMin(Math.round(raw / STEP_MIN) * STEP_MIN);
  }

  function paintGhost() {
    const g = ghostRef.current;
    if (!g || fromRef.current == null) return;
    const lo = Math.min(fromRef.current, toRef.current);
    const hi = Math.max(fromRef.current, toRef.current);
    g.style.display = "flex";
    g.style.top = `${(lo - DAY_START_MIN) * PX_PER_MIN}px`;
    g.style.height = `${Math.max(4, (hi - lo) * PX_PER_MIN)}px`;
    g.textContent = `${minToLabel(lo)}-${minToLabel(hi)}`;
  }

  function endDrag() {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    fromRef.current = null;
    if (ghostRef.current) ghostRef.current.style.display = "none";
  }

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    // Le drag ne démarre que sur le fond du rail, pas sur un bloc existant.
    if ((e.target as HTMLElement).closest(".tlday-block")) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    rectRef.current = e.currentTarget.getBoundingClientRect();
    const from = minFromClientY(e.clientY);
    fromRef.current = from;
    toRef.current = from;
    paintGhost();
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (fromRef.current == null) return;
    toRef.current = minFromClientY(e.clientY);
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      paintGhost();
    });
  }

  function onPointerUp() {
    if (fromRef.current == null) return;
    const slot = resolveSlotRange(fromRef.current, toRef.current);
    endDrag();
    if (slot) onCreateSlot(slot.start, slot.end);
  }

  const hours: number[] = [];
  for (let m = DAY_START_MIN; m <= DAY_END_MIN; m += 60) hours.push(m);

  return (
    <div className="tlday">
      <div className="tlday-hint">
        Glissez sur la timeline pour saisir un créneau (pas de 15 min). Appui
        simple : créneau d'une heure.
      </div>
      <div className="tlday-wrap" style={{ height }}>
        <div className="tlday-hours">
          {hours.map((m) => (
            <div
              key={m}
              className="tlday-hour"
              style={{ top: (m - DAY_START_MIN) * PX_PER_MIN }}
            >
              {minToLabel(m)}
            </div>
          ))}
        </div>
        <div
          ref={trackRef}
          className="tlday-track"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={endDrag}
        >
          {hours.map((m) => (
            <div
              key={m}
              className="tlday-grid"
              style={{ top: (m - DAY_START_MIN) * PX_PER_MIN }}
            />
          ))}
          {isToday && nowMin >= DAY_START_MIN && nowMin <= DAY_END_MIN && (
            <div
              className="tlday-now"
              style={{ top: (nowMin - DAY_START_MIN) * PX_PER_MIN }}
            />
          )}
          {laidBlocks.map((b, i) => (
            <div
              key={i}
              className={`tlday-block${b.live ? " live" : ""}${
                (b.endMin - b.startMin) * PX_PER_MIN < 26 ? " is-short" : ""
              }`}
              title={`${b.code || "Activité"} · ${minToLabel(b.startMin)}-${
                b.live ? "…" : minToLabel(b.endMin)
              }`}
              style={{
                top: (b.startMin - DAY_START_MIN) * PX_PER_MIN,
                height: Math.max(
                  MIN_BLOCK_PX,
                  (b.endMin - b.startMin) * PX_PER_MIN,
                ),
                ...laneStyle(b.lane, b.lanes),
              }}
            >
              <span className="code">{b.code || "Activité"}</span>
              <span className="range">
                {minToLabel(b.startMin)}-{b.live ? "…" : minToLabel(b.endMin)}
              </span>
              {(b.projects.length > 0 ||
                b.lteTasks.length > 0 ||
                b.tasks.length > 0) && (
                <span className="tlday-links">
                  {b.projects.map((p) => (
                    <span key={p.id} className="chip chip-pm">
                      {p.label}
                    </span>
                  ))}
                  {b.lteTasks.map((t) => (
                    <span key={t.id} className="chip chip-lte">
                      {t.label}
                    </span>
                  ))}
                  {b.tasks.map((t) => (
                    <span key={t.id} className="chip chip-tm">
                      {t.label}
                    </span>
                  ))}
                </span>
              )}
            </div>
          ))}
          {laidMarkers.map((mk, i) => (
            <div
              key={`m${i}`}
              className={`tlday-marker ${mk.type}`}
              style={{
                top: (mk.min - DAY_START_MIN) * PX_PER_MIN,
                height: MARKER_PX,
                ...laneStyle(mk.lane, mk.lanes),
              }}
              title={`${mk.label} · ${minToLabel(mk.min)}`}
            >
              <span className="label">{mk.label}</span>
              <span className="range">{minToLabel(mk.min)}</span>
            </div>
          ))}
          <div
            ref={ghostRef}
            className="tlday-ghost"
            style={{ display: "none" }}
          />
        </div>
      </div>
    </div>
  );
}
